<template>
    <div class="min-h-screen bg-gray-50 py-8">
        <div class="max-w-2xl mx-auto bg-white rounded-lg shadow-md">
            <!-- Header -->
            <div class="bg-blue-600 text-white p-6 rounded-t-lg">
                <h1 class="text-2xl font-bold">Commande en Ligne</h1>
                <p class="text-blue-100">{{ currentRestaurant.name }}</p>
            </div>

            <!-- Progress Steps -->
            <div class="px-6 py-4 border-b">
                <div class="flex justify-between">
                    <div v-for="(step, index) in steps" :key="step.id" class="flex flex-col items-center">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center mb-2 text-sm font-medium"
                             :class="getStepClass(step.id)">
                            {{ index + 1 }}
                        </div>
                        <span class="text-xs text-gray-600">{{ step.label }}</span>
                    </div>
                </div>
            </div>

            <!-- Step 1: Order Type -->
            <div v-if="currentStep === 1" class="p-6">
                <h2 class="text-xl font-semibold mb-4">Type de commande</h2>
                <div class="grid grid-cols-2 gap-4">
                    <div class="border-2 rounded-lg p-4 cursor-pointer transition-all"
                         :class="orderType === 'delivery' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'"
                         @click="orderType = 'delivery'">
                        <div class="text-center">
                            <svg class="w-12 h-12 mx-auto mb-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                            </svg>
                            <h3 class="font-semibold">Livraison</h3>
                            <p class="text-sm text-gray-600">Livré à votre adresse</p>
                        </div>
                    </div>
                    <div class="border-2 rounded-lg p-4 cursor-pointer transition-all"
                         :class="orderType === 'pickup' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'"
                         @click="orderType = 'pickup'">
                        <div class="text-center">
                            <svg class="w-12 h-12 mx-auto mb-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                            </svg>
                            <h3 class="font-semibold">À emporter</h3>
                            <p class="text-sm text-gray-600">Récupération au restaurant</p>
                        </div>
                    </div>
                </div>

                <div class="mt-6 flex justify-between">
                    <button @click="$router.back()" class="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600">
                        Retour
                    </button>
                    <button @click="nextStep" class="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
                        Continuer
                    </button>
                </div>
            </div>

            <!-- Step 2: Delivery Address -->
            <div v-if="currentStep === 2 && orderType === 'delivery'" class="p-6">
                <h2 class="text-xl font-semibold mb-4">Adresse de livraison</h2>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Zone de livraison</label>
                        <select v-model="selectedZone" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                            <option value="">Sélectionnez votre zone</option>
                            <option v-for="zone in deliveryZones" :key="zone.id" :value="zone">
                                {{ zone.name }} - {{ zone.deliveryTime }} - Frais: {{ zone.fee }}€
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Adresse complète</label>
                        <textarea v-model="deliveryAddress" rows="3"
                                  class="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="Numéro, rue, appartement..."></textarea>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                            <input v-model="postalCode" type="text"
                                   class="w-full px-3 py-2 border border-gray-300 rounded-md">
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                            <input v-model="city" type="text"
                                   class="w-full px-3 py-2 border border-gray-300 rounded-md">
                        </div>
                    </div>
                </div>

                <div class="mt-6 flex justify-between">
                    <button @click="prevStep" class="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600">
                        Retour
                    </button>
                    <button @click="nextStep" class="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
                        Continuer
                    </button>
                </div>
            </div>

            <!-- Step 3: Customer Info -->
            <div v-if="currentStep === 3" class="p-6">
                <h2 class="text-xl font-semibold mb-4">Informations client</h2>

                <div class="space-y-4">
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                            <input v-model="customer.firstName" type="text"
                                   class="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                            <input v-model="customer.lastName" type="text"
                                   class="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input v-model="customer.email" type="email"
                               class="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                        <input v-model="customer.phone" type="tel"
                               class="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                    </div>
                </div>

                <div class="mt-6 flex justify-between">
                    <button @click="prevStep" class="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600">
                        Retour
                    </button>
                    <button @click="nextStep" class="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
                        Continuer
                    </button>
                </div>
            </div>

            <!-- Step 4: Cart & Payment -->
            <div v-if="currentStep === 4" class="p-6">
                <h2 class="text-xl font-semibold mb-4">Récapitulatif & Paiement</h2>

                <!-- Order Summary -->
                <div class="bg-gray-50 rounded-lg p-4 mb-6">
                    <h3 class="font-semibold mb-3">Votre commande</h3>
                    <div v-for="item in cart" :key="item.id" class="flex justify-between py-2 border-b">
                        <div>
                            <span class="font-medium">{{ item.name }}</span>
                            <span class="text-gray-600 ml-2">x{{ item.quantity }}</span>
                        </div>
                        <span>{{ (item.price * item.quantity).toFixed(2) }}€</span>
                    </div>

                    <div class="space-y-2 mt-3 pt-3 border-t">
                        <div v-if="orderType === 'delivery' && selectedZone" class="flex justify-between text-sm">
                            <span>Frais de livraison</span>
                            <span>{{ selectedZone.fee }}€</span>
                        </div>
                        <div class="flex justify-between font-semibold text-lg">
                            <span>Total</span>
                            <span>{{ totalAmount }}€</span>
                        </div>
                    </div>
                </div>

                <!-- Payment Method -->
                <div class="mb-6">
                    <h3 class="font-semibold mb-3">Moyen de paiement</h3>
                    <div class="space-y-2">
                        <label class="flex items-center">
                            <input v-model="paymentMethod" type="radio" value="card" class="mr-2">
                            <span>Carte bancaire</span>
                        </label>
                        <label class="flex items-center">
                            <input v-model="paymentMethod" type="radio" value="paypal" class="mr-2">
                            <span>PayPal</span>
                        </label>
                        <label v-if="orderType === 'pickup'" class="flex items-center">
                            <input v-model="paymentMethod" type="radio" value="cash" class="mr-2">
                            <span>Paiement sur place</span>
                        </label>
                    </div>
                </div>

                <!-- Terms -->
                <div class="mb-6">
                    <label class="flex items-center">
                        <input v-model="acceptedTerms" type="checkbox" class="mr-2">
                        <span class="text-sm">J'accepte les conditions générales de vente</span>
                    </label>
                </div>

                <div class="flex justify-between">
                    <button @click="prevStep" class="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600">
                        Retour
                    </button>
                    <button @click="submitOrder"
                            :disabled="!acceptedTerms"
                            class="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:bg-gray-400">
                        Confirmer la commande
                    </button>
                </div>
            </div>

            <!-- Success Step -->
            <div v-if="currentStep === 5" class="p-6 text-center">
                <div class="text-green-600 mb-4">
                    <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <h2 class="text-2xl font-bold mb-2">Commande confirmée !</h2>
                <p class="text-gray-600 mb-4">Votre numéro de commande: <strong>{{ orderNumber }}</strong></p>
                <p class="text-sm text-gray-500 mb-6">
                    {{ orderType === 'delivery' ?
                    `Temps de livraison estimé: ${selectedZone.deliveryTime}` :
                    'Prêt dans environ 20-30 minutes' }}
                </p>
                <button @click="$router.push('/restaurantselector')" class="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
                    Retour à l'accueil
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions } from 'vuex';

    export default {
        name: 'OnlineOrderForm',
        data() {
            return {
                currentStep: 1,
                orderType: 'delivery',
                selectedZone: null,
                deliveryAddress: '',
                postalCode: '',
                city: '',
                customer: {
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: ''
                },
                paymentMethod: 'card',
                acceptedTerms: false,
                orderNumber: '',
                steps: [
                    { id: 'type', label: 'Type' },
                    { id: 'address', label: 'Adresse' },
                    { id: 'info', label: 'Infos' },
                    { id: 'payment', label: 'Paiement' }
                ]
            };
        },
        computed: {
            ...mapState(['currentRestaurant', 'products']),
            ...mapState('onlineOrders', ['deliveryZones']),
            cart() {
                // Simuler un panier - en pratique ça viendrait d'un autre module
                return [
                    { id: 1, name: 'Pizza Margherita', price: 12, quantity: 1 },
                    { id: 2, name: 'Coca-Cola', price: 3, quantity: 2 }
                ];
            },
            totalAmount() {
                const itemsTotal = this.cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                const deliveryFee = this.orderType === 'delivery' && this.selectedZone ? this.selectedZone.fee : 0;
                return (itemsTotal + deliveryFee).toFixed(2);
            }
        },
        methods: {
            ...mapActions('onlineOrders', ['createOnlineOrder']),

            getStepClass(stepId) {
                const stepIndex = this.steps.findIndex(step => step.id === stepId);
                const currentIndex = this.currentStep - 1;

                if (stepIndex < currentIndex) return 'bg-green-500 text-white';
                if (stepIndex === currentIndex) return 'bg-blue-500 text-white';
                return 'bg-gray-200 text-gray-500';
            },

            nextStep() {
                if (this.validateStep()) {
                    this.currentStep++;
                }
            },

            prevStep() {
                this.currentStep--;
            },

            validateStep() {
                switch (this.currentStep) {
                    case 1:
                        return this.orderType !== '';
                    case 2:
                        if (this.orderType === 'delivery') {
                            return this.selectedZone && this.deliveryAddress && this.postalCode && this.city;
                        }
                        return true;
                    case 3:
                        return this.customer.firstName && this.customer.lastName &&
                            this.customer.email && this.customer.phone;
                    default:
                        return true;
                }
            },

            async submitOrder() {
                try {
                    const orderData = {
                        type: this.orderType,
                        customer: this.customer,
                        items: this.cart,
                        total: parseFloat(this.totalAmount),
                        status: 'pending',
                        restaurantId: this.currentRestaurant.id
                    };

                    if (this.orderType === 'delivery') {
                        orderData.delivery = {
                            zone: this.selectedZone,
                            address: this.deliveryAddress,
                            postalCode: this.postalCode,
                            city: this.city
                        };
                        orderData.deliveryFee = this.selectedZone.fee;
                    }

                    const order = await this.createOnlineOrder(orderData);
                    this.orderNumber = order.orderNumber;
                    this.currentStep = 5;

                } catch (error) {
                    alert('Erreur lors de la création de la commande: ' + error.message);
                }
            }
        }
    };
</script>